<template>
  <div class="def-phone">
    <span class="def-phone__label">{{ placeholder }}</span>
    <input
      class="def-phone-hidden"
      type="text"
      :name="name"
      :value="savedValue"
      :required="required"
      @invalid.prevent="handlerInvalid"
    />
    <!--стоп для change не можна робити-->
    <input
      ref="input"
      class="def-intl-input"
      :class="{ 'def-intl-input_invalid': !isValid || isShowError }"
      type="tel"
      :value="inputedPhone"
      :readonly="readonly"
      @countrychange="countrychange"
      @input="handlerInput"
      @paste="handlerSaveValue"
      @change="handlerChange"
    />
    <span v-if="isShowError" class="def-phone-error">
      <slot />
    </span>
  </div>
</template>

<script lang="ts" setup>
import intlTelInput from 'intl-tel-input';
import type { SomeOptions, Iti } from 'intl-tel-input';
import { useStore } from '@/store';

import { computed, onMounted, ref, defineEmits, onBeforeUnmount } from 'vue';

const { state, commit } = useStore();

const props = defineProps<{
  onlyCountries?: string[];
  initialCountry?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  required?: boolean;
  isShowError?: boolean;
  readonly?: boolean;
}>();

const input = ref<HTMLInputElement | null>(null);
const phoneIsoCode = ref<string>('');
const countries = computed(() => state.results.deliveryCountries?.items);

let iti: Iti | null = null;
const isValid = ref(true);

defineExpose({
  phoneIsoCode,
});
const removePlus = (str: string | undefined) => str?.replace(/[+ ]/g, '') || '';

const savedValue = ref<string>(removePlus(props.value));

const emit = defineEmits(['countrychange', 'changePhone', 'inputPhone', 'completedPhone']);

const handlerSaveValue = () => {
  if (iti?.isValidNumberPrecise()) {
    savedValue.value = removePlus(iti?.getNumber());
    isValid.value = true;
    updateIsoCode();

    emit('completedPhone', savedValue.value);
  } else {
    savedValue.value = '';
    if (iti?.getNumber()?.length) {
      isValid.value = false;
    }
  }

  emit('inputPhone', iti?.getNumber());
};

const inputedPhone = ref(props.value);

const handlerInput = (e) => {
  // якщо країна не обрана блокуємо ввод телефону
  if (!iti?.getSelectedCountryData()?.iso2) {
    (e.target as HTMLInputElement).value = '';
    e.preventDefault();
    return;
  }

  handlerSaveValue();
  inputedPhone.value = e.target.value;
};

const handlerChange = () => {
  emit('changePhone', iti?.getNumber());
};

const countrychange = () => {
  handlerSaveValue();

  emit('countrychange', iti?.getSelectedCountryData()?.iso2);
};

const handlerInvalid = () => {
  isValid.value = false;
};
const updateIsoCode = () => {
  phoneIsoCode.value = iti?.getSelectedCountryData()?.iso2;
};

onMounted(() => {
  try {
    if (input.value) {
      const countriesCode = countries.value.map((el) => el.UF_COUNTRY_ISO_CODE);

      const options: SomeOptions = {
        utilsScript: `${__webpack_public_path__}intl-tel/utils.js`,
        strictMode: true,
        onlyCountries: countriesCode,
        initialCountry: props.initialCountry,
        placeholderNumberType: 'MOBILE',
      };

      if (props.onlyCountries) {
        options.onlyCountries = props.onlyCountries;
      }

      if (props.onlyCountries?.length === 1 || props.readonly) {
        options.allowDropdown = false;
      }

      if (!props.initialCountry && !props.value) {
        options.initialCountry = 'auto';
      }

      iti = intlTelInput(input.value, options);
      // якщо країна не передається робимо запит в ручну на отримання країни.
      if (iti?.options?.initialCountry === 'auto') {
        commit('global/loaderStart');
        fetch('https://ipapi.co/json')
          .then((res) => res.json())
          .then((data) => {
            if (countriesCode?.find((el) => el === data.country_code) || !countriesCode?.length) {
              iti?.setCountry(data.country_code);
              emit('countrychange', data.country_code);
            } else {
              // iti?.setCountry('ua');
              // emit('countrychange', 'ua');
            }
          })
          .catch(() => {
            // iti.setCountry('ua');
            // emit('countrychange', 'ua');
          })
          .finally(() => commit('global/loaderStop'));
      }

      updateIsoCode();
    }
  } catch (err) {
    console.error(err);
  }
});

onBeforeUnmount(() => {
  if (iti) {
    iti.destroy();
    iti = null;
  }
});
</script>

<style lang="scss" scoped>
.def-phone {
  position: relative;
  &__label {
    position: absolute;
    top: 8px;
    left: 100px;
    color: var(--color-dark-grey);
    transition: 0.2s linear top;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: calc(100% - 132px);
    overflow: hidden;
    font-size: 14px;
    font-weight: 500;
    z-index: 1;
  }
  :deep(.iti) {
    width: 100%;
  }
  .def-intl-input {
    height: 52px;
    border: 1px solid #c8c8c8;
    width: 100%;
    padding-left: 100px !important;
    &_invalid {
      border-color: var(--color-red) !important;
    }
  }
  :deep(input.def-intl-input) {
    padding-left: 100px;
    padding-top: 15px;
  }
  :deep(.iti__arrow) {
    border: none;
    background: url('data:image/svg+xml;utf8,<svg  viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"> <path fill-rule="evenodd" clip-rule="evenodd" d="M4.4328 7.78295C4.75987 7.45588 5.29016 7.45588 5.61723 7.78295L10.0501 12.2158L14.483 7.78295C14.8101 7.45588 15.3403 7.45588 15.6674 7.78295C15.9945 8.11002 15.9945 8.6403 15.6674 8.96737L10.6423 13.9925C10.3153 14.3195 9.78497 14.3195 9.4579 13.9925L4.4328 8.96737C4.10573 8.6403 4.10573 8.11002 4.4328 7.78295Z"/> </svg> ');
    width: 20px;
    height: 20px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transition: 0.2s linear transform;
    margin-left: 8px;
  }
  :deep(.iti__arrow--up) {
    transform: rotate(-180deg);
  }
  :deep(.iti__selected-country-primary) {
    border-right: 1px solid #c8c8c8;
    width: 84px;
    padding: 0 16px;
  }
  :deep(.iti__country-container) {
    left: 0;
    right: unset !important;
  }
}

.def-phone-hidden {
  display: none;
  &:valid + div .def-intl-input {
    border-color: var(--color-green);
  }
}
.def-phone-error {
  position: absolute;
  left: 16px;
  top: calc(100% + 4px);
  font-size: rem(10);
  line-height: 100%;
  color: var(--color-red);
}
:global(.iti--fullscreen-popup) {
  z-index: 10003;
}
:global(.iti__search-input) {
  height: 36px;
  min-height: 36px;
  padding: 0 16px;
}
</style>
